import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@repo/components';
import { MarkdownViewer } from '@nineeng/form-components';
import { identifyActor } from '@repo/shared/analytics';
import { getOverview, getUserLanguage } from 'src/api/access/overview';
import AccessLayoutWrapper from 'src/layouts/access';
import QRCodeAccordion from 'src/components/access/QRCodeAccordion';
import RoleWithTaskAccordion from 'src/components/access/RoleWithTasksAccordion';
import LoadingDots from 'src/components/access/LoadingDots';
import AccessAppLink from 'src/components/access/AccessAppLink';

type AccessAppLanguageKey = 'en' | 'nl' | 'fr' | 'de' | 'pl';

type TailwindClasses = {
	backGroundColor?: string;
	padding?: string;
	default?: string;
};

const createTailwindClassNames = (customClasses: TailwindClasses = {}) => {
	return Object.values(customClasses).filter(Boolean).join(' ');
};

export const Route = createFileRoute('/access_/overview')({
	component: OverviewPage,
});

function OverviewPage() {
	const search = useSearch({ strict: false }) as { lng?: string };
	const [qrTimestamp, setQrTimestamp] = useState(Date.now());
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const { data: overview } = useQuery({
		queryKey: ['userOverview'],
		queryFn: getOverview,
		retry: false,
		useErrorBoundary: false,
	});

	const { data: userLanguage } = useQuery({
		queryKey: ['userLanguage'],
		queryFn: getUserLanguage,
		retry: false,
		useErrorBoundary: false,
	});

	const revalidateOverview = () => {
		queryClient.invalidateQueries({ queryKey: ['userOverview'] });
	};

	useEffect(() => {
		const intervalId = setInterval(() => {
			revalidateOverview();
			setQrTimestamp(Date.now());
		}, 10000);

		return () => clearInterval(intervalId);
	}, []);

	useEffect(() => {
		identifyActor(overview?.user);
	}, [overview?.user?.id]);

	if (!overview || !userLanguage) {
		return <LoadingDots />;
	}

	const language = (search.lng || userLanguage) as AccessAppLanguageKey;
	const {
		themeSettings,
		site,
		user,
		rolesWithTasks,
		accessAppSettings,
		showManagerAccess,
		markedForReview,
		hasInvalidGroup,
		languagesSettings,
	} = overview;

	return (
		<AccessLayoutWrapper
			themeSettings={themeSettings}
			languagesSettings={languagesSettings}
			site={site}
			userLanguage={language}
		>
			<div className="mt-8 mx-auto w-full md:w-[768px] flex flex-col md:flex-row gap-8 px-4 md:px-0">
				<div className="flex flex-col gap-4 md:w-[250px] flex-shrink-0">
					<div
						className={createTailwindClassNames({
							backGroundColor: `bg-[${themeSettings?.colors?.logoBackground}]`,
							padding: `p-[${themeSettings?.logoPadding}px]`,
							default: 'max-w-sm rounded-2xl overflow-hidden mx-auto, max-h-40',
						})}
					>
						<img src={themeSettings?.logo?.link} alt={site?.name} className="w-full h-full object-contain" />
					</div>
					{showManagerAccess && (
						<div className="hidden md:flex flex-col gap-y-2">
							<h5 className="font-bold">{t('LBL_MANAGER_ACCESS')}:</h5>
							<span className="">{t('LBL_MANAGER_ACCESS_HELP')}</span>
							<AccessAppLink text={t('LBL_MANAGER_PORTAL')} href="/" />
						</div>
					)}
					<div className="hidden md:flex flex-col gap-y-2">
						{accessAppSettings?.[language]?.Links && accessAppSettings?.[language]?.Links?.length > 0 && (
							<>
								<h4 className="text-h4 font-bold">{t('LBL_LINKS')}:</h4>
								{accessAppSettings?.[language]?.Links.map((link: { label?: string; url?: string }, index: number) => (
									<AccessAppLink key={index} text={link.label || ''} href={link?.url || ''} />
								))}
							</>
						)}
					</div>
				</div>

				<div className="flex flex-col w-full max-w-[500px] gap-4">
					<div>
						<h3 className="text-h3 font-bold mb-4">
							{t('LBL_HI')} {user?.firstName} {user?.lastName}
						</h3>
						{markedForReview && (
							<div className="mb-4 mx-auto max-w-3xl px-4 md:px-0">
								<Alert variant={'warning'}>{t('LBL_REVIEWED')}</Alert>
							</div>
						)}
						{hasInvalidGroup && (
							<div className="mb-4 mx-auto max-w-3xl px-4 md:px-0">
								<Alert variant={'warning'}>{t('LBL_INVALID_GROUP')}</Alert>
							</div>
						)}
						<div className="nine-task-overrides">
							<MarkdownViewer value={accessAppSettings?.[language]?.welcomeMessage} />
						</div>
					</div>
					<RoleWithTaskAccordion rolesWithTasks={rolesWithTasks} language={language} />
					{overview?.showQRCode && (
						<QRCodeAccordion data={overview} timestamp={qrTimestamp} inReview={markedForReview} />
					)}
				</div>
			</div>

			<div className="flex justify-center mt-12">
				<span className="text-sm text-gray-400">
					{t('LBL_POWERED_BY') + ' '}
					<a
						href="https://www.nineid.com"
						target="_blank"
						rel="noopener noreferrer"
						className="underline hover:text-gray-600"
					>
						NineID
					</a>
				</span>
			</div>
		</AccessLayoutWrapper>
	);
}
